// components/b2b/Catalog.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { useBrand } from '../../contexts/BrandContext';
import { useToast } from '../../contexts/ToastContext';
import { useCart } from '../../contexts/CartContext';
import { productService } from '../../services/productService';
import { CATALOG, CATALOG_CATEGORIES } from '../../config/catalog';

const PRODUCTS_PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 400;

function mapStaticProduct(item) {
  return {
    id: item.id,
    sku: item.id,
    title: item.name,
    source: item.manufacturer,
    packaging: item.unit,
    thumbnailUrl: null,
    gallery: [],
    price: { price: item.direct, salePrice: null, retailPrice: item.gpo },
    categories: [item.category],
    requiredLicense: item.dscsa,
    description: '',
    availability: 'in_stock',
    isActive: true,
    _isStatic: true,
    _staticEmoji: item.emoji,
    _staticOrigin: item.origin
  };
}

function ProductImage({ product }) {
  const [imgError, setImgError] = useState(false);
  const imageUrl = product.thumbnailUrl || (product.gallery && product.gallery[0]);

  if (product._isStatic || !imageUrl || imgError) {
    return (
      <div style={{
        width: 64,
        height: 64,
        borderRadius: 8,
        background: '#F3F4F6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontSize: product._isStatic ? 36 : 14,
        color: '#9CA3AF'
      }}>
        {product._isStatic && product._staticEmoji ? product._staticEmoji : (
          <Icon name="package" size={28} color="#9CA3AF" />
        )}
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={product.title}
      onError={() => setImgError(true)}
      style={{
        width: 64,
        height: 64,
        objectFit: 'cover',
        borderRadius: 8,
        flexShrink: 0,
        background: '#F3F4F6'
      }}
    />
  );
}

export function Catalog({ onNavigate }) {
  const { brand } = useBrand();
  const toast = useToast();
  const { addItem, itemCount } = useCart();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState(CATALOG_CATEGORIES);
  const [resultCount, setResultCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingFallback, setUsingFallback] = useState(false);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [page, setPage] = useState(1);
  const debounceRef = useRef(null);
  const [retryCount, setRetryCount] = useState(0);

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleCategoryChange = useCallback((e) => {
    setCategory(e.target.value);
    setPage(1);
  }, []);

  const handleRetry = useCallback(() => {
    setRetryCount(c => c + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchProducts() {
      setLoading(true);
      setError(null);

      try {
        const params = {
          page,
          limit: PRODUCTS_PER_PAGE
        };
        if (debouncedSearch.trim()) {
          params.phrase = debouncedSearch.trim();
        }
        if (category !== 'All') {
          params.categories = [category];
        }

        const result = await productService.searchProducts(params);

        if (cancelled) return;

        const hits = result.hits || [];
        setProducts(hits);
        setResultCount(result.resultCount || hits.length);
        setUsingFallback(false);

        if (result.filters && result.filters.categories) {
          const dynamicCats = result.filters.categories.map(c => c.name).filter(Boolean);
          if (dynamicCats.length > 0) {
            setCategories(['All', ...dynamicCats]);
          }
        }
      } catch (err) {
        if (cancelled) return;
        console.warn('Product API unavailable, using static catalog:', err.message);
        setError(err.message);
        setUsingFallback(true);

        const mapped = CATALOG.map(mapStaticProduct);
        const filtered = mapped.filter(p => {
          const matchesSearch = !debouncedSearch.trim() ||
            p.title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
            p.source.toLowerCase().includes(debouncedSearch.toLowerCase());
          const matchesCategory = category === 'All' || p.categories.includes(category);
          return matchesSearch && matchesCategory;
        });
        setProducts(filtered);
        setResultCount(filtered.length);
        setCategories(CATALOG_CATEGORIES);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchProducts();

    return () => { cancelled = true; };
  }, [debouncedSearch, category, page, retryCount]);

  const addToCart = (product) => {
    addItem(product, 1);
    toast.addToast('Added to purchase order');
  };

  const totalPages = Math.ceil(resultCount / PRODUCTS_PER_PAGE);

  return (
    <div style={{
      background: '#F9FAFB',
      minHeight: '100vh',
      padding: '40px 24px',
      fontFamily: 'Libre Franklin, sans-serif'
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'start',
          marginBottom: 32,
          flexWrap: 'wrap',
          gap: 16
        }}>
          <div>
            <h1 style={{ fontSize: 32, fontWeight: 700, marginBottom: 8, color: brand.c.navy }}>
              Procurement Catalog
            </h1>
            <p style={{ color: '#6B7280' }}>
              {loading ? 'Loading products...' : `${resultCount} products`} • Direct wholesale pricing
              {usingFallback && (
                <span style={{ color: '#F59E0B', marginLeft: 8, fontSize: 12 }}>
                  (offline mode)
                </span>
              )}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button icon="link" onClick={() => onNavigate('vendors')}>
              Manage Vendors
            </Button>
            {itemCount > 0 && (
              <Button variant="accent" icon="clipboard" onClick={() => onNavigate('cart')}>
                Create PO ({itemCount})
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card style={{ marginBottom: 24 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: 16,
            alignItems: 'end'
          }}>
            <div style={{ position: 'relative' }}>
              <Icon
                name="search"
                size={20}
                color="#9CA3AF"
                style={{
                  position: 'absolute',
                  left: 14,
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}
              />
              <input
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder="Search products, manufacturers..."
                style={{
                  width: '100%',
                  padding: '12px 16px 12px 44px',
                  border: '2px solid #E5E7EB',
                  borderRadius: 8,
                  fontSize: 14,
                  fontFamily: 'Libre Franklin, sans-serif',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <select
              value={category}
              onChange={handleCategoryChange}
              style={{
                padding: '12px 16px',
                border: '2px solid #E5E7EB',
                borderRadius: 8,
                fontSize: 14,
                fontFamily: 'Libre Franklin, sans-serif',
                cursor: 'pointer',
                minWidth: 150
              }}
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </Card>

        {/* Loading State */}
        {loading && (
          <Card>
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{
                width: 48,
                height: 48,
                border: '4px solid #E5E7EB',
                borderTop: `4px solid ${brand.c.accent}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }} />
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              <p style={{ color: '#6B7280', fontSize: 16 }}>Loading products...</p>
            </div>
          </Card>
        )}

        {/* Offline Fallback Banner */}
        {!loading && usingFallback && (
          <div style={{
            padding: '12px 16px',
            background: '#FEF3C7',
            border: '1px solid #FDE68A',
            borderRadius: 8,
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8
          }}>
            <span style={{ fontSize: 14, color: '#92400E' }}>
              Showing offline catalog. Live product data is temporarily unavailable.
            </span>
            <Button size="sm" onClick={handleRetry}>
              Retry
            </Button>
          </div>
        )}

        {/* Products Grid */}
        {!loading && products.length === 0 ? (
          <Card>
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <div style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: '#F3F4F6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px'
              }}>
                <Icon name="search" size={40} color="#9CA3AF" />
              </div>
              <h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 12 }}>
                No Products Found
              </h3>
              <p style={{ color: '#6B7280', marginBottom: 24 }}>
                Try adjusting your search or filters
              </p>
            </div>
          </Card>
        ) : !loading && (
          <div style={{ display: 'grid', gap: 16 }}>
            {products.map(product => {
              const price = product.price || {};
              const currentPrice = price.price || 0;
              const retailPrice = price.retailPrice || 0;
              const hasSavings = retailPrice > 0 && retailPrice > currentPrice;
              const savingsPercent = hasSavings
                ? Math.round(((retailPrice - currentPrice) / retailPrice) * 100)
                : 0;

              const productSku = product.sku || product.id;
              const productCategory = (product.categories && product.categories[0]) || '';
              const productSource = product.source || '';

              return (
                <Card key={product.id || product.sku} hover>
                  <div style={{ display: 'flex', gap: 20, alignItems: 'start' }}>
                    <ProductImage product={product} />

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'start',
                        justifyContent: 'space-between',
                        marginBottom: 12,
                        gap: 16
                      }}>
                        <div style={{ flex: 1 }}>
                          <h3 style={{
                            fontSize: 18,
                            fontWeight: 700,
                            marginBottom: 4,
                            color: brand.c.navy
                          }}>
                            {product.title}
                          </h3>
                          <div style={{ fontSize: 14, color: '#6B7280', marginBottom: 4 }}>
                            {productSource}{productSource && productCategory ? ' \u2022 ' : ''}{productCategory}
                          </div>
                          <div style={{
                            display: 'inline-block',
                            padding: '2px 8px',
                            background: '#F3F4F6',
                            borderRadius: 4,
                            fontSize: 12,
                            color: '#6B7280',
                            fontWeight: 600
                          }}>
                            SKU: {productSku}
                          </div>
                        </div>

                        <Button
                          size="sm"
                          icon="cart"
                          onClick={() => addToCart(product)}
                        >
                          Add to PO
                        </Button>
                      </div>

                      {/* Price Display */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                        padding: 16,
                        background: '#F9FAFB',
                        borderRadius: 8,
                        flexWrap: 'wrap'
                      }}>
                        {hasSavings && (
                          <div>
                            <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>
                              Retail Price
                            </div>
                            <div style={{
                              fontSize: 16,
                              fontWeight: 700,
                              color: '#EF4444',
                              textDecoration: 'line-through'
                            }}>
                              ${retailPrice.toFixed(2)}
                            </div>
                          </div>
                        )}

                        {price.salePrice !== null && price.salePrice !== undefined && price.salePrice > 0 && price.salePrice !== currentPrice && (
                          <div>
                            <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>
                              Sale Price
                            </div>
                            <div style={{
                              fontSize: 16,
                              fontWeight: 700,
                              color: '#F59E0B'
                            }}>
                              ${price.salePrice.toFixed(2)}
                            </div>
                          </div>
                        )}

                        <div>
                          <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>
                            Your Price
                          </div>
                          <div style={{
                            fontSize: 20,
                            fontWeight: 900,
                            color: '#10B981'
                          }}>
                            {currentPrice > 0 ? `$${currentPrice.toFixed(2)}` : 'Contact for pricing'}
                          </div>
                        </div>

                        {hasSavings && savingsPercent > 0 && (
                          <div style={{
                            padding: '6px 12px',
                            background: '#D1FAE5',
                            color: '#065F46',
                            borderRadius: 6,
                            fontSize: 14,
                            fontWeight: 700,
                            marginLeft: 'auto'
                          }}>
                            Save {savingsPercent}%
                          </div>
                        )}
                      </div>

                      {/* Additional Info */}
                      <div style={{
                        display: 'flex',
                        gap: 16,
                        marginTop: 12,
                        fontSize: 12,
                        color: '#6B7280',
                        flexWrap: 'wrap'
                      }}>
                        {product._isStatic && product._staticOrigin && (
                          <div>
                            <strong>Origin:</strong> {product._staticOrigin}
                          </div>
                        )}
                        {product.packaging && (
                          <div>
                            <strong>Unit:</strong> {product.packaging}
                          </div>
                        )}
                        {product.availability && product.availability !== 'in_stock' && (
                          <div>
                            <strong>Availability:</strong> {product.availability}
                          </div>
                        )}
                        {product.requiredLicense && (
                          <div style={{ color: '#10B981', fontWeight: 600 }}>
                            DSCSA Tracked
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!loading && !usingFallback && totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 12,
            marginTop: 32
          }}>
            <Button
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <span style={{ fontSize: 14, color: '#6B7280' }}>
              Page {page} of {totalPages}
            </span>
            <Button
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
